import React, { useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { motionSafe as motion } from "@/lib/motionSafe";
import { Link, useLocation } from "react-router-dom";
import { Check, X, Sparkles, FileText, Phone, Gauge, Map, Clock, Shield } from "lucide-react";
import { toast } from "sonner";
import { ConsentCheckboxes } from "./ConsentCheckboxes";
import { useI18n } from "../../i18n";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "nl_offer_popup_dismissed_v1";

const DELIVERABLES_DE = [
  { icon: FileText, title: "Produkt-Assessment gegen CRA-Verordnung", desc: "Rechtliche Einordnung & Scope-Analyse nach EU 2024/2847" },
  { icon: FileText, title: "Schriftlicher Bericht (PDF)", desc: "Priorisierter Maßnahmenplan mit Fristen" },
  { icon: Phone, title: "1 Stunde Experten-Call", desc: "Mag. Michael Jesse, LL.M. — persönlich" },
  { icon: Gauge, title: "CRA Readiness Scorecard", desc: "Ihr aktueller Compliance-Stand auf einen Blick" },
  { icon: Map, title: "Ihre persönliche CRA-Roadmap bis 2027", desc: "Konkrete Schritte zur Konformität bis Dezember 2027" },
  { icon: Clock, title: "Lieferung in 5 Werktagen", desc: "Nach Eingang aller Produktunterlagen" },
];

const DELIVERABLES_EN = [
  { icon: FileText, title: "Product assessment against the CRA regulation", desc: "Legal scoping & applicability under EU 2024/2847" },
  { icon: FileText, title: "Written report (PDF)", desc: "Prioritised action plan with deadlines" },
  { icon: Phone, title: "1-hour expert call", desc: "Mag. Michael Jesse, LL.M. — in person" },
  { icon: Gauge, title: "CRA readiness scorecard", desc: "Your current compliance posture at a glance" },
  { icon: Map, title: "Your personal CRA roadmap to 2027", desc: "Concrete steps to conformity by December 2027" },
  { icon: Clock, title: "Delivery in 5 business days", desc: "After receipt of all product documentation" },
];

export const OfferPopup = () => {
  const { lang } = useI18n();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(false); // becomes true once dismissed → shows FAB
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [consentProcessing, setConsentProcessing] = useState(false);
  const [consentMarketing, setConsentMarketing] = useState(false);
  const [consentAgb, setConsentAgb] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Do NOT auto-open the €990 Offer popup anymore.
  // The Free-or-Pay flows are separate:
  //   · CRA Applicability Checker (€0/€99) → homepage section #assessment
  //   · CRA-Ersteinschätzung €990         → this OfferPopup, opened ONLY via
  //                                          its own FAB button
  // Previously the popup opened after 5 s on the homepage which visually
  // hijacked the CRA-Check launch button in the same viewport.
  useEffect(() => {
    setSeen(true); // show FAB only; never auto-open the modal
  }, []);

  // ESC key closes the popup
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const close = () => {
    setOpen(false);
    setSeen(true);
    localStorage.setItem(STORAGE_KEY, "1");
  };

  const openAgain = () => setOpen(true);

  const L = lang === "en" ? {
    badge: "One-off · No subscription",
    title: "CRA Initial Assessment",
    priceSuffix: "excl. VAT · one-off",
    section1: "What you get",
    section2: "Book now",
    first: "First name",
    last: "Last name",
    email: "Email address",
    company: "Company & product (short)",
    companyPh: "e.g. SmartLock Ltd — connected door lock",
    submit: "Book now — € 990 →",
    submitting: "Submitting…",
    agb1: "I accept the ",
    agbLink: "General Terms and Conditions",
    agb2: ".",
    agbReq: "You must accept the terms to book.",
    money: "If we conclude after the assessment that your product requires no CRA action, we refund the full amount.",
    fab: "€ 990 · CRA Assessment",
    success: "Booked. Check your inbox — we contact you within 1 business day.",
    error: "Booking failed. Please try again.",
    deliverables: DELIVERABLES_EN,
  } : {
    badge: "Einmalig · Kein Abo",
    title: "CRA-Ersteinschätzung",
    priceSuffix: "zzgl. MwSt. · einmalig",
    section1: "Was Sie erhalten",
    section2: "Jetzt buchen",
    first: "Vorname",
    last: "Nachname",
    email: "E-Mail-Adresse",
    company: "Unternehmen & Produkt (kurz)",
    companyPh: "z.B. SmartLock GmbH — vernetztes Türschloss",
    submit: "Jetzt buchen — € 990 →",
    submitting: "Wird gesendet…",
    agb1: "Ich akzeptiere die ",
    agbLink: "Allgemeinen Geschäftsbedingungen",
    agb2: ".",
    agbReq: "Bitte AGB akzeptieren, um zu buchen.",
    money: "Wenn wir nach dem Assessment keinen CRA-Handlungsbedarf für Ihr Produkt feststellen, erstatten wir den vollen Betrag.",
    fab: "€ 990 · CRA-Ersteinschätzung",
    success: "Gebucht. Prüfen Sie Ihr Postfach — wir melden uns innerhalb 1 Werktages.",
    error: "Buchung fehlgeschlagen. Bitte erneut versuchen.",
    deliverables: DELIVERABLES_DE,
  };

  const submit = async (e) => {
    e.preventDefault();
    setShowErrors(true);
    if (!first.trim() || !last.trim() || !email.trim() || !company.trim() || !consentProcessing || !consentAgb) return;
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND}/api/leads/offer-booking`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          offer: "cra_initial_assessment",
          price_eur: 990,
          name: `${first.trim()} ${last.trim()}`,
          email: email.trim(),
          company: company.trim(),
          locale: lang,
          consent_processing: consentProcessing,
          consent_marketing: consentMarketing,
          consent_agb: consentAgb,
        }),
      });
      if (!r.ok) throw new Error("bad status");
      toast.success(L.success);
      close();
    } catch (err) {
      toast.error(L.error);
    } finally {
      setSubmitting(false);
    }
  };

  const field = "w-full bg-white/[0.03] border border-white/10 focus:border-[#0052FF] text-white placeholder:text-gray-400 text-sm px-4 py-3 rounded-sm outline-none transition-colors";

  return (
    <>
      {/* Floating pulse trigger (visible once dismissed) */}
      <AnimatePresence>
        {seen && !open && (
          <motion.button
            initial={{ opacity: 0, scale: 0.7, x: 40 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            exit={{ opacity: 0, scale: 0.7, x: 40 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            onClick={openAgain}
            data-testid="offer-fab"
            data-prerender-strip="true"
            className="fixed right-4 md:right-6 bottom-24 md:bottom-8 z-40 group"
            aria-label={L.fab}
          >
            {/* Multi-layer pulsing halo — stronger, more prominent */}
            <span className="absolute inset-0 rounded-full bg-[#0052FF] animate-ping opacity-70" style={{ animationDuration: "1.6s" }} />
            <span className="absolute inset-0 rounded-full bg-[#3B82F6] animate-ping opacity-50" style={{ animationDuration: "2.2s", animationDelay: "0.4s" }} />
            <span className="absolute -inset-3 rounded-full bg-[#0052FF]/40 blur-2xl animate-pulse" />
            <span className="absolute -inset-6 rounded-full bg-[#3B82F6]/25 blur-3xl animate-pulse" style={{ animationDuration: "2.4s" }} />
            {/* Solid button with subtle scale-pulse */}
            <motion.span
              animate={{ scale: [1, 1.06, 1] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              className="relative flex items-center gap-2 bg-[#0052FF] hover:bg-[#2563EB] text-white font-medium text-sm pl-3 pr-4 py-3 rounded-full shadow-2xl shadow-[#0052FF]/60 ring-2 ring-white/20 transition-colors"
            >
              <Sparkles size={16} />
              <span className="hidden sm:inline font-mono-tech text-xs tracking-wider uppercase">{L.fab}</span>
              <span className="sm:hidden font-mono-tech text-xs tracking-wider uppercase">€ 990</span>
            </motion.span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={close}
            data-testid="offer-popup"
          >
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.98 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-2xl max-h-[92vh] flex flex-col bg-[#030712] border border-white/10 rounded-sm overflow-hidden shadow-2xl"
            >
              {/* Header block — solid brand blue (fixed at top, never scrolls) */}
              <div className="relative bg-gradient-to-br from-[#0052FF] to-[#2563EB] px-5 sm:px-8 md:px-10 py-6 md:py-8 overflow-hidden shrink-0">
                <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-white/10 blur-3xl pointer-events-none" />
                <div className="relative flex items-start justify-between gap-3 md:gap-4">
                  <div className="min-w-0">
                    <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-white/70">
                      NexTech.Law
                    </div>
                    <h2 className="mt-2 font-display text-2xl sm:text-3xl md:text-4xl font-extrabold text-white leading-[1.05] tracking-tight">
                      {L.title}
                    </h2>
                    <div className="mt-4 flex items-baseline gap-3">
                      <span className="font-display text-4xl md:text-5xl font-extrabold text-white leading-none tracking-tight">€ 990</span>
                      <span className="text-white/80 text-sm">{L.priceSuffix}</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-3 shrink-0">
                    <button
                      onClick={close}
                      aria-label={lang === "de" ? "Schließen" : "Close"}
                      data-testid="offer-popup-close"
                      className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 border border-white/25 text-white text-xs font-mono-tech tracking-[0.15em] uppercase px-2.5 sm:px-3 py-2 rounded-full transition-colors"
                    >
                      <X size={14} />
                      <span className="hidden sm:inline">{lang === "de" ? "Schließen" : "Close"}</span>
                    </button>
                    <span className="hidden sm:inline-block bg-white/15 border border-white/20 text-white text-xs font-mono-tech tracking-[0.15em] uppercase px-3 py-1.5 rounded-full whitespace-nowrap">
                      {L.badge}
                    </span>
                  </div>
                </div>
              </div>

              {/* Body — internally scrollable so the header always stays visible */}
              <div className="px-6 md:px-10 py-6 md:py-8 overflow-y-auto flex-1">
                <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-gray-400 mb-6">
                  {L.section1}
                </div>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                  {L.deliverables.map((d, i) => {
                    const Icon = d.icon;
                    return (
                      <li key={i} className="flex items-start gap-3">
                        <span className="mt-0.5 shrink-0 w-6 h-6 rounded-sm bg-[#0052FF]/15 border border-[#0052FF]/30 flex items-center justify-center">
                          <Check size={13} className="text-[#3B82F6]" strokeWidth={3} />
                        </span>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-white leading-snug">{d.title}</div>
                          <div className="mt-0.5 text-xs text-gray-400 leading-relaxed">{d.desc}</div>
                        </div>
                      </li>
                    );
                  })}
                </ul>

                <div className="my-8 border-t border-white/10" />

                {/* Booking form */}
                <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-gray-400 mb-6">
                  {L.section2}
                </div>
                <form onSubmit={submit} className="space-y-4" data-testid="offer-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-400 mb-1.5 block">{L.first}</label>
                      <input data-testid="offer-first" className={field} value={first} onChange={(e) => setFirst(e.target.value)} placeholder="Max" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-1.5 block">{L.last}</label>
                      <input data-testid="offer-last" className={field} value={last} onChange={(e) => setLast(e.target.value)} placeholder="Mustermann" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1.5 block">{L.email}</label>
                    <input data-testid="offer-email" type="email" className={field} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="max@unternehmen.at" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1.5 block">{L.company}</label>
                    <input data-testid="offer-company" className={field} value={company} onChange={(e) => setCompany(e.target.value)} placeholder={L.companyPh} />
                  </div>

                  {/* Consent block — identical structure to contact form + AGB */}
                  <div className="pt-2 space-y-4">
                    <ConsentCheckboxes
                      processing={consentProcessing}
                      marketing={consentMarketing}
                      onProcessingChange={setConsentProcessing}
                      onMarketingChange={setConsentMarketing}
                      showError={showErrors}
                      testIdPrefix="offer-consent"
                    />

                    {/* AGB acceptance */}
                    <label
                      className="flex items-start gap-3 cursor-pointer group select-none"
                      data-testid="offer-consent-agb"
                      onClick={(e) => {
                        if (e.target.closest && e.target.closest("a")) return;
                        e.preventDefault();
                        setConsentAgb(!consentAgb);
                      }}
                    >
                      <span
                        role="checkbox"
                        aria-checked={consentAgb}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === " " || e.key === "Enter") {
                            e.preventDefault();
                            setConsentAgb(!consentAgb);
                          }
                        }}
                        className={`mt-0.5 shrink-0 w-5 h-5 rounded-sm border transition-colors flex items-center justify-center ${
                          consentAgb ? "bg-[#0052FF] border-[#0052FF]" : "bg-white/5 border-white/25 group-hover:border-white/50"
                        }`}
                      >
                        {consentAgb && <Check size={14} className="text-white" strokeWidth={3} />}
                      </span>
                      <span className="text-xs md:text-sm text-gray-400 leading-relaxed">
                        {L.agb1}
                        <Link to="/agb" target="_blank" className="text-[#3B82F6] underline underline-offset-2 hover:text-white transition-colors">
                          {L.agbLink}
                        </Link>
                        {L.agb2}
                        <span className="text-red-400 ml-1" aria-hidden="true">*</span>
                      </span>
                    </label>
                    {showErrors && !consentAgb && (
                      <p data-testid="offer-consent-agb-error" className="text-xs text-red-400 -mt-2 pl-8">
                        {L.agbReq}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="offer-submit"
                    className="mt-2 w-full trace-btn inline-flex items-center justify-center gap-2 bg-[#0052FF] hover:bg-[#2563EB] disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium px-6 py-4 rounded-sm transition-colors"
                  >
                    {submitting ? L.submitting : L.submit}
                  </button>

                  {/* Money-back guarantee */}
                  <div className="mt-4 flex items-start gap-3 bg-[#0052FF]/5 border border-[#0052FF]/20 rounded-sm px-4 py-3">
                    <Shield size={16} className="text-[#3B82F6] shrink-0 mt-0.5" />
                    <p className="text-xs text-gray-400 leading-relaxed">{L.money}</p>
                  </div>

                  {/* Soft decline — signals the modal is truly dismissible */}
                  <div className="pt-2 text-center">
                    <button
                      type="button"
                      onClick={close}
                      data-testid="offer-popup-decline"
                      className="text-xs text-gray-400 hover:text-white underline underline-offset-4 transition-colors"
                    >
                      {lang === "de" ? "Vielleicht später · ESC drücken zum Schließen" : "Maybe later · Press ESC to close"}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
